/**
 * Shopping List business rules
 *
 * Pure functions with no platform or I/O dependencies.
 * Usable in both backend services and future mobile clients.
 */

import { IngredientType } from '../enums/IngredientType';
import { ItemSource } from '../enums/ItemSource';
import { MealSlot } from '../enums/MealSlot';
import {
  isConvertibleUnit,
  toBaseUnit,
  toDisplayUnit,
  BaseUnit,
  CONVERSION_TO_BASE,
} from './unitConversion';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ScaledIngredient {
  /** Ingredient name (as stored on the recipe) */
  name: string;
  /** Original quantity string from recipe (e.g. "200", "1.5", "al gusto") */
  quantity: string | null;
  /** Unit of measurement (e.g. "g", "ml", null) */
  unit: string | null;
  /** ID of the contributing recipe */
  recipeId: string;
  /** Type of ingredient — used to choose appropriate unit conversion */
  ingredientType?: IngredientType;
  /** True when this entry comes from a directly-added planner ingredient, not a recipe — excluded from totalRecipeCount */
  isDirect?: boolean;
}

/** A single quantity entry within an ingredient group (e.g. "200 g" or "2 cups") */
export interface IngredientSubItem {
  quantity: string | null;
  unit: string | null;
  /** Number of distinct recipes contributing this quantity/unit pair */
  recipeCount: number;
}

/**
 * A grouped ingredient for display.
 * If entries.length === 1, render as a flat row.
 * If entries.length > 1, render as a parent row with sub-items.
 */
export interface IngredientGroup {
  /** Canonical ingredient name (preserves casing of first occurrence) */
  name: string;
  /** All quantity/unit entries for this ingredient */
  entries: IngredientSubItem[];
  /** Total number of distinct recipes contributing to this group */
  totalRecipeCount: number;
}

// ─── aggregateIngredients ────────────────────────────────────────────────────

/**
 * Aggregates a flat list of scaled ingredients into grouped, normalized display items.
 *
 * Algorithm:
 *  1. Group by normalized (lowercase, trimmed) ingredient name.
 *  2. Within each name group:
 *     a. Convertible entries (g/kg/oz/lb, ml/L/fl_oz) sharing the same base unit
 *        are converted, summed, and converted back to the most readable metric unit.
 *     b. Non-convertible entries are grouped by exact unit. Same unit + numeric
 *        quantities are summed; non-numeric quantities become separate sub-items.
 *  3. Return IngredientGroup[] — flat when entries.length === 1, grouped otherwise.
 */
export function aggregateIngredients(
  ingredients: ScaledIngredient[],
): IngredientGroup[] {
  if (ingredients.length === 0) return [];

  // Step 1: Group by normalized name, preserving first-seen canonical name
  const nameGroups = new Map<string, { canonicalName: string; items: ScaledIngredient[] }>();
  for (const ingredient of ingredients) {
    const nameKey = ingredient.name.toLowerCase().trim();
    const existing = nameGroups.get(nameKey);
    if (!existing) {
      nameGroups.set(nameKey, { canonicalName: ingredient.name, items: [ingredient] });
    } else {
      existing.items.push(ingredient);
    }
  }

  const result: IngredientGroup[] = [];

  for (const { canonicalName, items } of nameGroups.values()) {
    const totalRecipeCount = new Set(
      items.filter((i) => !i.isDirect).map((i) => i.recipeId),
    ).size;
    const entries: IngredientSubItem[] = [];

    // Step 2a: Separate convertible items with numeric quantities from the rest
    const massBucket: Array<{ qty: number; unit: string }> = [];
    const volumeBucket: Array<{ qty: number; unit: string }> = [];
    const fallbackItems: ScaledIngredient[] = [];

    for (const item of items) {
      if (isConvertibleUnit(item.unit)) {
        const qty = parseFloat(item.quantity ?? '');
        if (Number.isFinite(qty) && item.unit) {
          const conv = CONVERSION_TO_BASE[item.unit];
          if (conv?.base === BaseUnit.Mass) {
            massBucket.push({ qty, unit: item.unit });
          } else {
            volumeBucket.push({ qty, unit: item.unit });
          }
        } else {
          // Convertible unit but non-numeric quantity → treat as non-convertible
          fallbackItems.push(item);
        }
      } else {
        fallbackItems.push(item);
      }
    }

    // Sum mass bucket
    if (massBucket.length > 0) {
      let totalBase = 0;
      for (const { qty, unit } of massBucket) {
        const base = toBaseUnit(qty, unit);
        if (base) totalBase += base.value;
      }
      const { quantity, unit } = toDisplayUnit(totalBase, BaseUnit.Mass);
      entries.push({ quantity, unit, recipeCount: massBucket.length });
    }

    // Sum volume bucket
    if (volumeBucket.length > 0) {
      let totalBase = 0;
      for (const { qty, unit } of volumeBucket) {
        const base = toBaseUnit(qty, unit);
        if (base) totalBase += base.value;
      }
      const { quantity, unit } = toDisplayUnit(totalBase, BaseUnit.Volume);
      entries.push({ quantity, unit, recipeCount: volumeBucket.length });
    }

    // Step 2b: Non-convertible items — group by exact unit, sum numeric quantities
    type NcBucket = { quantity: string | null; unit: string | null; recipeCount: number; _sum: number | null };
    const ncBuckets = new Map<string, NcBucket>();

    for (const item of fallbackItems) {
      const unitKey = item.unit ?? '__null__';
      const existing = ncBuckets.get(unitKey);
      const parsed = parseFloat(item.quantity ?? '');
      const isNumeric = Number.isFinite(parsed);

      if (!existing) {
        ncBuckets.set(unitKey, {
          quantity: item.quantity,
          unit: item.unit,
          recipeCount: 1,
          _sum: isNumeric ? parsed : null,
        });
      } else if (existing._sum !== null && isNumeric) {
        existing._sum += parsed;
        existing.quantity = String(parseFloat(existing._sum.toFixed(10)));
        existing.recipeCount += 1;
      } else {
        // Cannot merge — add as a separate entry with a unique key
        const fallbackKey = `${unitKey}||${ncBuckets.size}`;
        ncBuckets.set(fallbackKey, {
          quantity: item.quantity,
          unit: item.unit,
          recipeCount: 1,
          _sum: isNumeric ? parsed : null,
        });
      }
    }

    for (const { _sum: _, ...entry } of ncBuckets.values()) {
      entries.push(entry);
    }

    result.push({ name: canonicalName, entries, totalRecipeCount });
  }

  return result;
}

// ─── groupShoppingListItems ──────────────────────────────────────────────────

/**
 * Groups a flat list of persisted shopping list items by ingredient name for display.
 *
 * - Planner-source items with the same name are grouped together.
 *   Same-unit entries within a group are summed; different units become sub-items.
 * - Manual-source items are never grouped — each becomes a standalone single-entry group.
 * - Items are stored with already-normalized quantities (from preview aggregation),
 *   so no unit conversion is needed here.
 */
export interface ShoppingListItemInput {
  id: string;
  name: string;
  quantity: string | null;
  unit: string | null;
  purchased: boolean;
  source: ItemSource;
  recipeCount: number | null;
}

export function groupShoppingListItems(
  items: ShoppingListItemInput[],
  locale = 'en',
): IngredientGroup[] {
  const result: IngredientGroup[] = [];

  // Manual items are returned as standalone single-entry groups
  const manualItems = items.filter((i) => i.source === ItemSource.Manual);
  const plannerItems = items.filter((i) => i.source !== ItemSource.Manual);

  for (const item of manualItems) {
    result.push({
      name: item.name,
      entries: [{ quantity: item.quantity, unit: item.unit, recipeCount: item.recipeCount ?? 1 }],
      totalRecipeCount: item.recipeCount ?? 1,
    });
  }

  // Group planner items by normalized name
  const nameGroups = new Map<string, { canonicalName: string; items: ShoppingListItemInput[] }>();
  for (const item of plannerItems) {
    const nameKey = item.name.toLowerCase().trim();
    const existing = nameGroups.get(nameKey);
    if (!existing) {
      nameGroups.set(nameKey, { canonicalName: item.name, items: [item] });
    } else {
      existing.items.push(item);
    }
  }

  for (const { canonicalName, items: groupItems } of nameGroups.values()) {
    const totalRecipeCount = groupItems.reduce((sum, i) => sum + (i.recipeCount ?? 1), 0);

    // Sum same-unit entries
    type UnitBucket = { quantity: string | null; unit: string | null; recipeCount: number; _sum: number | null };
    const unitBuckets = new Map<string, UnitBucket>();

    for (const item of groupItems) {
      const unitKey = item.unit ?? '__null__';
      const existing = unitBuckets.get(unitKey);
      const parsed = parseFloat(item.quantity ?? '');
      const isNumeric = Number.isFinite(parsed);

      if (!existing) {
        unitBuckets.set(unitKey, {
          quantity: item.quantity,
          unit: item.unit,
          recipeCount: item.recipeCount ?? 1,
          _sum: isNumeric ? parsed : null,
        });
      } else if (existing._sum !== null && isNumeric) {
        existing._sum += parsed;
        existing.quantity = String(parseFloat(existing._sum.toFixed(10)));
        existing.recipeCount += item.recipeCount ?? 1;
      } else {
        const fallbackKey = `${unitKey}||${unitBuckets.size}`;
        unitBuckets.set(fallbackKey, {
          quantity: item.quantity,
          unit: item.unit,
          recipeCount: item.recipeCount ?? 1,
          _sum: isNumeric ? parsed : null,
        });
      }
    }

    const entries: IngredientSubItem[] = Array.from(unitBuckets.values()).map(
      ({ _sum: _, ...entry }) => entry,
    );

    result.push({ name: canonicalName, entries, totalRecipeCount });
  }

  return sortIngredientGroupsByName(result, locale);
}

/**
 * Sorts ingredient groups alphabetically by name, case- and accent-aware per
 * the given locale. Ties (identical names, case/accent-insensitive) retain
 * their original relative order thanks to native stable sort.
 *
 * @param groups Ingredient groups to sort (not mutated — a new array is returned).
 * @param locale BCP 47 locale tag driving collation rules (defaults to 'en').
 */
export function sortIngredientGroupsByName(
  groups: IngredientGroup[],
  locale = 'en',
): IngredientGroup[] {
  const collator = new Intl.Collator(locale, { sensitivity: 'accent' });
  return [...groups].sort((a, b) => collator.compare(a.name, b.name));
}

/**
 * Calculates how many recipe batches are needed to feed the given number of
 * people for the number of days the recipe is planned.
 *
 * Formula: ceil((people × daysPlanned) / recipeServings)
 *
 * Examples:
 *  - 1 person, recipe serves 2, planned 1 day  → ceil(1/2) = 1 (leftovers)
 *  - 2 people, recipe serves 2, planned 2 days → ceil(4/2) = 2
 *  - 4 people, recipe serves 2, planned 1 day  → ceil(4/2) = 2
 *
 * @param people       Number of people eating (minimum 1)
 * @param daysPlanned  Number of times this recipe appears in the planner week
 * @param recipeServings Recipe's declared serving count (0 or negative → treated as 1)
 */
export function calculateBatches(
  people: number,
  daysPlanned: number,
  recipeServings: number,
): number {
  const servings = recipeServings > 0 ? recipeServings : 1;
  return Math.ceil((people * daysPlanned) / servings);
}

/**
 * Scales a directly-added planner ingredient entry's quantity by the number of
 * people in a shopping-list generation request. Unlike recipe-ingredient scaling
 * (calculateBatches), there is no servings/days concept — each entry independently
 * contributes quantity × people (FR-008).
 *
 * @param quantity The entry's stored quantity string, or null if omitted.
 * @param people   Number of people eating.
 */
export function scaleDirectIngredientQuantity(quantity: string | null, people: number): string | null {
  if (quantity === null) return null;
  const parsed = parseFloat(quantity);
  if (!Number.isFinite(parsed)) return quantity;
  return String(parseFloat((parsed * people).toFixed(10)));
}

// ─── sortOccurrencesByDateAndSlot ───────────────────────────────────────────

const MEAL_SLOT_ORDER: Record<MealSlot, number> = {
  [MealSlot.Breakfast]: 0,
  [MealSlot.Lunch]: 1,
  [MealSlot.Snack]: 2,
  [MealSlot.Dinner]: 3,
};

export interface SortableOccurrence {
  plannedDate: string;
  mealSlot: MealSlot;
}

/**
 * Orders planner occurrences chronologically by day, then by meal slot within
 * the day (Breakfast → Lunch → Snack → Dinner), so the generated shopping
 * list wizard mirrors the weekly planner's layout.
 */
export function sortOccurrencesByDateAndSlot<T extends SortableOccurrence>(occurrences: T[]): T[] {
  return [...occurrences].sort((a, b) => {
    if (a.plannedDate !== b.plannedDate) return a.plannedDate < b.plannedDate ? -1 : 1;
    return MEAL_SLOT_ORDER[a.mealSlot] - MEAL_SLOT_ORDER[b.mealSlot];
  });
}

/**
 * Resolves the effective people count for a single planned occurrence:
 * the matching override's peopleCount if present, else defaultPeopleCount.
 */
export function resolveOccurrencePeopleCount(
  occurrenceId: string,
  overrides: { occurrenceId: string; peopleCount: number }[],
  defaultPeopleCount: number,
): number {
  return overrides.find((o) => o.occurrenceId === occurrenceId)?.peopleCount ?? defaultPeopleCount;
}
